/**
 * VisitaFichaPage — rota /visitas/:id (RF-M8-001/004/005, US-08).
 * Split-view: visualizador (zoom/pan) à esquerda; campos editáveis (com as
 * explicações da validação sob cada campo) + "Regras aplicadas" + histórico +
 * ações à direita (ADR-030). Edição inline por clique (editor/admin), com validação local
 * M4-TS e comentário opcional; revalidação é feita pelo backend (M4 síncrono).
 * Perfil `consulta` → ficha somente leitura (RN-GLOBAL-006). Conflito 409 →
 * ConflictBanner; 503 → aviso. Nenhum dado pessoal é logado (RN-M11-001).
 * Reprocessamento (ADR-036, design §5): "Reprocessar esta visita" (admin) abre o diálogo único; em andamento,
 * "Reprocessando esta visita…" com o status acompanhado até concluir e a ficha recarregada; depois, o bloco
 * "Último reprocessamento" (quando, ação, marcador antes → depois, campos que mudaram ou "nada mudou") ou a falha.
 * ADR-038 (design §9): a etiqueta da evidência no cabeçalho; na visita sem imagem, no lugar do visualizador, a origem
 * na planilha (arquivo, aba, linha; "Referência — há termo com imagem" com o link para a escola) e sem "Baixar
 * original"/"Copiar caminho"; na com imagem cuja escola consta da planilha, o aviso com o link para essa linha.
 * ADR-048 (spec evidencias-complementares, design §5): sob o documento, "Outras evidências"; clicar numa evidência (ou
 * no "ver evidência" de um achado) a abre no visualizador, que alterna "Termo"/"Evidência"; os campos do equipamento
 * num grupo "Equipamento", com os rótulos (fechado quando nada foi lido nele).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useVisita } from '../hooks/useConsulta';
import { reprocessamentoEmAndamento, useStatusReprocessamento } from '../hooks/usePainel';
import { useEditarVisita, useConferir, useHistorico } from '../hooks/useEdicao';
import { useServices } from '../contexts/ServicesContext';
import { useAuth, hasAnyProfile } from '../contexts/AuthContext';
import { DocViewer } from '../components/DocViewer';
import { MarcadorPill, NomeEscola } from '../components/ConsultaShared';
import { formatarData, paginaDoArquivo, rotuloTipoDocumento } from '../components/format';
import { EditableField } from '../components/EditableField';
import { RegrasAplicadasPanel } from '../components/RegrasAplicadasPanel';
import { ConferirButton } from '../components/ConferirButton';
import { HistoricoPanel } from '../components/HistoricoPanel';
import { ConflictBanner } from '../components/ConflictBanner';
import { ReprocessarDialog } from '../components/ReprocessarDialog';
import { ConflictError, ServiceUnavailableError } from '../services/edicaoApi';
import { CampoFicha, EscolaDaVisita, OrigemPlanilha, UltimoReprocessamento } from '../services/consulta-types';
import { EtiquetaEvidencia, ROTULO_REFERENCIA } from '../components/Evidencia';
import { RelatorioFotograficoSecao } from '../components/RelatorioFotograficoSecao';
import { OutrasEvidenciasSecao, arquivoDaEvidencia, tituloDaEvidencia } from '../components/OutrasEvidenciasSecao';
import { VerEvidencia, VerEvidenciaContext } from '../contexts/VerEvidenciaContext';
import { CAMPOS_EQUIPAMENTO, rotuloCampo } from '../services/rotulos-campos';

type Aviso = { readonly tipo: 'ok' | 'erro' | 'conflito'; readonly texto: string } | null;

const NOMES_EQUIPAMENTO: ReadonlySet<string> = new Set(CAMPOS_EQUIPAMENTO);

/** Os campos da ficha separados: os do termo (na ordem da API) e os do equipamento (na ordem do grupo — ADR-048). */
function separarCampos(campos: readonly CampoFicha[]): { principais: CampoFicha[]; equipamento: CampoFicha[] } {
  return {
    principais: campos.filter((c) => !NOMES_EQUIPAMENTO.has(c.nomeCampo)),
    equipamento: CAMPOS_EQUIPAMENTO.flatMap((nome) => campos.filter((c) => c.nomeCampo === nome)),
  };
}

const lido = (v: string | null): boolean => v !== null && v.trim() !== '';

export function VisitaFichaPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const visitaId = id ?? '';
  const { ficha, carregando, erro } = useVisita(visitaId || null);
  const historico = useHistorico(visitaId || null);
  const { consulta } = useServices();
  const auth = useAuth();
  const podeEditar = hasAnyProfile(auth, ['editor', 'admin']);
  const podeReprocessar = hasAnyProfile(auth, ['admin']);

  const { editar, salvando: salvandoEdicao } = useEditarVisita(visitaId);
  const { conferir, salvando: salvandoConferir } = useConferir(visitaId);

  const [aviso, setAviso] = useState<Aviso>(null);
  const [conflito, setConflito] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [reprocessarAberto, setReprocessarAberto] = useState(false);
  const [disparadoId, setDisparadoId] = useState<string | null>(null);

  // ADR-048: a evidência complementar aberta no visualizador (a última escolhida) e se o visualizador a mostra agora
  // ("Evidência") ou mostra o termo ("Termo"). Outra visita: volta ao termo.
  const evidencias = ficha?.evidenciasComplementares;
  const [evidenciaAberta, setEvidenciaAberta] = useState<string | null>(null);
  const [mostrarEvidencia, setMostrarEvidencia] = useState(false);
  const documentoRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setEvidenciaAberta(null);
    setMostrarEvidencia(false);
  }, [visitaId]);
  const abrirEvidencia = useCallback((evidenciaId: string): void => {
    setEvidenciaAberta(evidenciaId);
    setMostrarEvidencia(true);
    // Escolhida na seção ou num achado, longe do visualizador: o visualizador vem à vista.
    documentoRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, []);
  const verEvidencia = useMemo<VerEvidencia | null>(
    () => (evidencias && evidencias.length > 0 ? { ids: new Set(evidencias.map((e) => e.id)), abrir: abrirEvidencia } : null),
    [evidencias, abrirEvidencia],
  );
  const evidenciaNoVisualizador = mostrarEvidencia ? (evidencias ?? []).find((e) => e.id === evidenciaAberta) ?? null : null;

  // Reprocessamento em andamento: o disparado aqui ou o último da ficha que ainda não gravou esta visita. O status
  // (rota só do administrador) é acompanhado até concluir; aí a ficha e o histórico recarregam uma vez.
  const qc = useQueryClient();
  const ultimo = ficha?.ultimoReprocessamento ?? null;
  const pendenteNaFicha = ultimo !== null && ultimo.resultado === null && reprocessamentoEmAndamento(ultimo);
  const acompanhado = disparadoId ?? (pendenteNaFicha ? ultimo.reprocessamentoId : null);
  const { status: statusReprocessamento } = useStatusReprocessamento(podeReprocessar ? acompanhado : null);
  const terminou = statusReprocessamento !== undefined && !reprocessamentoEmAndamento(statusReprocessamento);
  const reprocessando = podeReprocessar && acompanhado !== null ? !terminou : pendenteNaFicha;
  const reprocessamentoFalhou =
    !reprocessando && ultimo !== null && ultimo.resultado === null && !reprocessamentoEmAndamento(ultimo);

  useEffect(() => {
    if (!terminou || acompanhado === null) {
      return;
    }
    void qc.invalidateQueries({ queryKey: ['visita', visitaId] });
    void qc.invalidateQueries({ queryKey: ['historico', visitaId] });
  }, [terminou, acompanhado, visitaId, qc]);

  // O row_version corrente vem da ficha (contrato GET /visitas/{id} → row_version).
  const rowVersion = ficha?.row_version ?? 0;

  const tratarErroEscrita = (e: unknown): void => {
    if (e instanceof ConflictError) {
      setConflito(true);
      setAviso({ tipo: 'conflito', texto: 'Conflito de versão. Recarregue a ficha.' });
    } else if (e instanceof ServiceUnavailableError) {
      setAviso({ tipo: 'erro', texto: 'Validação indisponível. Nenhuma alteração foi salva. Tente novamente.' });
    } else {
      setAviso({ tipo: 'erro', texto: 'Não foi possível salvar a alteração.' });
    }
  };

  const salvarCampo = async (
    campo: string,
    valor: string | null,
    comentario?: string,
  ): Promise<boolean> => {
    setAviso(null);
    try {
      await editar({ row_version: rowVersion, campos: [{ nome: campo, valor, comentario }] });
      setAviso({ tipo: 'ok', texto: 'Correção salva. Regras reavaliadas.' });
      return true;
    } catch (e) {
      tratarErroEscrita(e);
      return false; // mantém o campo em edição; o erro já virou aviso na UI
    }
  };

  const alternarConferida = async (): Promise<void> => {
    setAviso(null);
    try {
      await conferir(rowVersion);
      setAviso({ tipo: 'ok', texto: 'Conferência registrada.' });
    } catch (e) {
      tratarErroEscrita(e);
    }
  };

  const recarregar = (): void => {
    setConflito(false);
    setAviso(null);
    void historico; // o cache é invalidado pelos hooks; recarrega ao limpar o banner
    window.location.reload();
  };

  const copiarCaminho = async (): Promise<void> => {
    if (!ficha?.caminhoOriginal) {
      return; // visita sem imagem: não há caminho (ADR-038)
    }
    try {
      await navigator.clipboard.writeText(ficha.caminhoOriginal);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  const baixarOriginal = async (): Promise<void> => {
    if (!visitaId) {
      return;
    }
    setBaixando(true);
    try {
      const { downloadUrl } = await consulta.baixarOriginal(visitaId);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      setAviso({ tipo: 'erro', texto: 'Não foi possível gerar o link de download.' });
    } finally {
      setBaixando(false);
    }
  };

  return (
    <div>
      <p className="breadcrumb">
        <Link to="/visitas">Visitas</Link>
        <span className="sep">/</span>
        <span className="mono">{visitaId}</span>
      </p>

      {!podeEditar && <p className="muted">Perfil Consulta: somente leitura.</p>}
      {conflito && <ConflictBanner onRecarregar={recarregar} />}
      {aviso && (
        <p className={aviso.tipo === 'ok' ? 'notice notice-ok' : 'notice'} role="status">
          {aviso.texto}
        </p>
      )}

      {reprocessando && (
        <p className="notice notice-info" role="status">
          Reprocessando esta visita…
        </p>
      )}
      {reprocessamentoFalhou && (
        <p className="notice" role="status">
          O reprocessamento desta visita falhou. A visita continua como estava.
        </p>
      )}

      {carregando && <p className="muted">Carregando…</p>}
      {erro && <p className="notice">Não foi possível carregar a visita.</p>}

      {ficha && (
        <>
          {ficha.escola && <LinhaEscola escola={ficha.escola} />}
          {ficha.planilhaReferencia && (
            <p className="notice notice-info">
              Esta escola também consta da planilha de instalações (sem imagem).{' '}
              <Link to={`/visitas/${ficha.planilhaReferencia.visitaId}`}>Ver a linha da planilha</Link>
            </p>
          )}
          <VerEvidenciaContext.Provider value={verEvidencia}>
          <div className="split">
            <div>
              <div className="card" ref={documentoRef} data-testid="documento">
                <div className="documento-cabecalho">
                  <h2>Documento</h2>
                  {evidencias && evidencias.length > 0 && (
                    <div className="alternar-documento" role="group" aria-label="Mostrar no visualizador">
                      <button
                        type="button"
                        className={evidenciaNoVisualizador ? 'btn btn-ghost' : 'btn btn-primary'}
                        aria-pressed={!evidenciaNoVisualizador}
                        onClick={() => setMostrarEvidencia(false)}
                      >
                        {ficha.planilha ? 'Planilha' : 'Termo'}
                      </button>
                      <button
                        type="button"
                        className={evidenciaNoVisualizador ? 'btn btn-primary' : 'btn btn-ghost'}
                        aria-pressed={!!evidenciaNoVisualizador}
                        // A última aberta (se ainda está na lista); senão, a primeira.
                        onClick={() => abrirEvidencia(evidencias.find((e) => e.id === evidenciaAberta)?.id ?? evidencias[0].id)}
                      >
                        Evidência
                      </button>
                    </div>
                  )}
                </div>
                {evidenciaNoVisualizador ? (
                  <>
                    <p className="muted pagina-arquivo">
                      {`${tituloDaEvidencia(evidenciaNoVisualizador)} — ${arquivoDaEvidencia(evidenciaNoVisualizador)}`}
                    </p>
                    <DocViewer
                      viewerUrl={evidenciaNoVisualizador.visualizadorUrl}
                      alt={rotuloTipoDocumento(evidenciaNoVisualizador.tipo)}
                    />
                  </>
                ) : ficha.planilha ? (
                  <OrigemNaPlanilha origem={ficha.planilha} inep={ficha.escola?.inep ?? ficha.inep} />
                ) : (
                  <>
                    {paginaDoArquivo(ficha) && <p className="muted pagina-arquivo">{paginaDoArquivo(ficha)}</p>}
                    <DocViewer viewerUrl={ficha.visualizadorUrl} />
                  </>
                )}
              </div>

              {evidencias && (
                <OutrasEvidenciasSecao
                  evidencias={evidencias}
                  aberta={evidenciaNoVisualizador?.id ?? null}
                  onAbrir={abrirEvidencia}
                />
              )}
            </div>

            <div>
              <section className="card">
                <h2>
                  Campos <MarcadorPill marcador={ficha.marcador} />
                  {ficha.conferida && <span className="pill pill-ok">conferida</span>}{' '}
                  <EtiquetaEvidencia
                    evidencia={ficha.evidencia}
                    referencia={ficha.planilha?.referencia === true}
                    fonte={ficha.planilha?.fonte}
                  />
                </h2>
                <CamposDaFicha
                  campos={ficha.campos}
                  somenteLeitura={!podeEditar}
                  onSalvar={salvarCampo}
                />
                {podeEditar && (
                  <div className="acoes">
                    <ConferirButton
                      conferida={ficha.conferida}
                      salvando={salvandoConferir}
                      onConferir={alternarConferida}
                    />
                    {podeReprocessar && (
                      <button type="button" className="btn btn-ghost" onClick={() => setReprocessarAberto(true)}>
                        Reprocessar esta visita
                      </button>
                    )}
                    {salvandoEdicao && <span className="muted">Salvando…</span>}
                  </div>
                )}
              </section>

              <RegrasAplicadasPanel campos={ficha.campos} />

              {ultimo?.resultado && <UltimoReprocessamentoBloco modo={ultimo.modo} resultado={ultimo.resultado} />}

              <section className="card">
                <h2>Histórico</h2>
                <HistoricoPanel
                  entradas={historico.entradas}
                  carregando={historico.carregando}
                  erro={historico.erro}
                />
              </section>
            </div>
          </div>
          </VerEvidenciaContext.Provider>

          {ficha.relatorioFotografico && <RelatorioFotograficoSecao rf={ficha.relatorioFotografico} />}

          {ficha.caminhoOriginal !== null && (
          <section className="card">
            <h2>Arquivo original</h2>
            <div className="path-box">
              <span className="caminho mono">{ficha.caminhoOriginal}</span>
              <button className="btn btn-ghost" onClick={copiarCaminho}>
                {copiado ? 'Copiado!' : 'Copiar caminho'}
              </button>
              <button className="btn btn-primary" onClick={baixarOriginal} disabled={baixando}>
                {baixando ? 'Gerando link…' : 'Baixar original'}
              </button>
            </div>
            {typeof ficha.pagina === 'number' && (
              <p className="muted">
                O original é o arquivo inteiro{ficha.paginasArquivo ? ` (${ficha.paginasArquivo} páginas)` : ''}: a
                visita está na página {ficha.pagina}.
              </p>
            )}
            {ficha.caminhosDuplicados.length > 1 && (
              <p className="muted">
                Este conteúdo aparece em {ficha.caminhosDuplicados.length} caminhos do pendrive.
              </p>
            )}
          </section>
          )}
        </>
      )}

      {reprocessarAberto && (
        <ReprocessarDialog
          alvo={{ tipo: 'visita', visitaId }}
          onFechar={() => setReprocessarAberto(false)}
          onDisparado={(resposta) => setDisparadoId(resposta.reprocessamentoId)}
        />
      )}
    </div>
  );
}

/**
 * Os campos da ficha: os do termo, como a API os manda; os do equipamento (ADR-048, [OPEN-1]) num grupo "Equipamento"
 * com os rótulos, na ordem do design — aberto quando algum foi lido (num termo 4G, nada: o grupo fica fechado). Numa API
 * anterior (sem esses campos), não há o grupo.
 */
function CamposDaFicha({
  campos,
  somenteLeitura,
  onSalvar,
}: {
  readonly campos: readonly CampoFicha[];
  readonly somenteLeitura: boolean;
  readonly onSalvar: (campo: string, valor: string | null, comentario?: string) => Promise<boolean>;
}): JSX.Element {
  const { principais, equipamento } = separarCampos(campos);
  const campo = (c: CampoFicha, rotulo?: string): JSX.Element => (
    <EditableField
      key={c.nomeCampo}
      nomeCampo={c.nomeCampo}
      rotulo={rotulo}
      valorLiteral={c.valorLiteral}
      valorFinal={c.valorFinal}
      marcador={c.marcador}
      editadoPorUsuario={c.editadoPorUsuario}
      achados={c.achados ?? []}
      somenteLeitura={somenteLeitura}
      onSalvar={onSalvar}
    />
  );
  return (
    <>
      <div className="campo-list">
        {campos.length === 0 && <p className="muted">Nenhum campo extraído.</p>}
        {principais.map((c) => campo(c))}
      </div>
      {equipamento.length > 0 && (
        <details
          className="campo-grupo"
          data-testid="grupo-equipamento"
          open={equipamento.some((c) => lido(c.valorFinal) || lido(c.valorLiteral))}
        >
          <summary>Equipamento</summary>
          <div className="campo-list">{equipamento.map((c) => campo(c, rotuloCampo(c.nomeCampo)))}</div>
        </details>
      )}
    </>
  );
}

/**
 * O que o último reprocessamento fez nesta visita (ADR-036, Req 2.3): quando, a ação, o marcador antes → depois e
 * os campos cujo valor ou marcador mudou — só os nomes, nunca os valores; sem mudança, "Nada mudou.".
 */
function UltimoReprocessamentoBloco({
  modo,
  resultado,
}: {
  readonly modo: UltimoReprocessamento['modo'];
  readonly resultado: NonNullable<UltimoReprocessamento['resultado']>;
}): JSX.Element {
  const acao = modo === 'reler' ? 'Lida de novo' : 'Conferida de novo';
  const campos = resultado.camposAlterados;
  const nadaMudou = campos.length === 0 && resultado.marcadorAntes === resultado.marcadorDepois;
  return (
    <section className="card" data-testid="ultimo-reprocessamento">
      <h2>Último reprocessamento</h2>
      <p>{`${acao} em ${formatarData(resultado.gravadoEm)}`}</p>
      <p data-testid="marcador-antes-depois">
        Marcador <MarcadorPill marcador={resultado.marcadorAntes} /> → <MarcadorPill marcador={resultado.marcadorDepois} />
      </p>
      {nadaMudou && <p className="muted">Nada mudou.</p>}
      {campos.length > 0 && <p>{`Campos que mudaram: ${campos.map(rotuloCampo).join(', ')}`}</p>}
    </section>
  );
}

/**
 * A origem da visita sem imagem (ADR-038, Req 5.3): no lugar do visualizador, de onde veio — arquivo, aba e linha da
 * planilha de instalações (nunca valores de célula). Na linha de referência, a marca e o link para a escola, onde está
 * a visita com imagem (o termo prevalece — Req 4).
 */
/** ADR-046: de que planilha vieram os dados da visita sem cópia. */
const DADOS_DA_FONTE: Readonly<Record<NonNullable<OrigemPlanilha['fonte']>, string>> = {
  instalacoes: 'dados da planilha de instalações',
  controle: 'dados da planilha de controle',
  relatorio_fotografico: 'dados da capa do Relatório Fotográfico',
};

function OrigemNaPlanilha({ origem, inep }: { readonly origem: OrigemPlanilha; readonly inep: string | null }): JSX.Element {
  const rf = origem.fonte === 'relatorio_fotografico';
  return (
    <div data-testid="origem-planilha">
      <p className="notice notice-info">{`Planilha (sem imagem) — ${DADOS_DA_FONTE[origem.fonte ?? 'instalacoes']}`}</p>
      <table className="table">
        <tbody>
          <tr>
            <th>Arquivo</th>
            <td className="mono">{origem.arquivo}</td>
          </tr>
          {rf ? (
            // O RF não tem linha: a "aba" diz de onde veio o INEP (a capa ou o nome do arquivo).
            <tr>
              <th>INEP lido de</th>
              <td>{origem.aba}</td>
            </tr>
          ) : (
            <>
              <tr>
                <th>Aba</th>
                <td>{origem.aba}</td>
              </tr>
              <tr>
                <th>Linha</th>
                <td>{origem.linha}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {origem.referencia && (
        <p>
          <span className="pill pill-evidencia-referencia">{ROTULO_REFERENCIA}</span> O termo digitalizado prevalece: esta
          linha fica só para consulta.{' '}
          {inep && <Link to={`/escolas/${inep}`}>Ver a escola (a visita com imagem)</Link>}
        </p>
      )}
    </div>
  );
}

/**
 * Cabeçalho com a escola da visita (ADR-035, design §5): o nome como lido no termo, o oficial
 * do INEP quando difere e o INEP — com link para a ficha da escola quando está no catálogo.
 */
function LinhaEscola({ escola }: { readonly escola: EscolaDaVisita }): JSX.Element {
  const temNome = !!(escola.nomeTermo || escola.nomeOficial);
  return (
    <div className="escola-linha" data-testid="escola-da-visita">
      <span className="escola-linha-rotulo">Escola</span>
      <NomeEscola termo={escola.nomeTermo} oficial={escola.nomeOficial} reserva={escola.inep} />
      {temNome && escola.inep && (
        <span className="escola-linha-inep">
          INEP{' '}
          {escola.nomeOficial ? (
            <Link className="mono" to={`/escolas/${escola.inep}`}>
              {escola.inep}
            </Link>
          ) : (
            <span className="mono">{escola.inep}</span>
          )}
        </span>
      )}
    </div>
  );
}
